"use client";
import { AppLinks, formatToNaira, IMedia, IProperties, pxToRem } from "@/util";
import { Box, Divider, Paper, Typography, Button } from "@mui/material";
import React from "react";
import { makeStyles } from "tss-react/mui";
import Image from "next/image";
import { useState } from "react";
import { AppColors } from "@/assets/colors";
import { useRouter } from "next/navigation";

type Props = {
  data: IProperties;
  link: string;
  showOverlayButtons?: boolean;
  isLink?: boolean;
  price: string;
  sendProperty?: () => void;
};

export default function ListCard({
  data,
  link,
  sendProperty,
  price,
  showOverlayButtons = true,
}: Partial<Props>) {
  const footData: { icon: string; name: string }[] = [
    { name: `${data?.totalbedrooms || 0} Beds`, icon: "Bed.svg" },
    { name: data?.totalbathrooms ? "Ensuite" : "No Ensuite", icon: "bath.svg" },
    { name: `${data?.squaremeter || 0} m²`, icon: "SquareMeters.svg" },
  ];
  const router = useRouter();
  const { classes } = styles();
  const [over, setOver] = useState(false);

  return (
    <Paper
      className={classes.container}
      elevation={2}
      onClick={() =>
        link
          ? router.push(link || "/projects/properties/listings/1")
          : sendProperty && sendProperty()
      }
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
    >
      <Box style={{ position: "relative", height: "200px" }}>
        <Image
          src={(data?.media[0] as IMedia)?.url || "/placeholder.jpg"}
          objectFit="cover"
          fill
          alt="Property image"
        />
      </Box>

      <Box className={classes.bottom}>
        <Box className={classes.col}>
          {Number(price) > 0 && (
            <Typography className={classes.price}>
              {formatToNaira(Number.parseInt(`${price}`) || 0)}
            </Typography>
          )}
          <Typography className={classes.name}>{data?.name || ""}</Typography>
          <Typography className={classes.desc}>
            {data?.address || ""}
          </Typography>
        </Box>
        <Divider />
        <Box className={classes.foot}>
          {footData.map((item, index) => (
            <Box className={classes.row} key={index}>
              <Image
                src={`/images/listings/${item.icon}`}
                width={20}
                height={20}
                alt="Property listings"
              />
              <Typography className={classes.bedTxt}>{item.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {showOverlayButtons && (
        <Box
          className={`${classes.overlay} ${over ? classes.overlayVisible : ""}`}
        >
          <Box className={classes.overlayBtn}>
            <Button
              href={AppLinks.PUBLIC.CONSULTATION}
              variant="contained"
              fullWidth
              className={classes.btn1}
            >
              Book Consultation
            </Button>

            <Button
              variant="contained"
              fullWidth
              href={link}
              className={classes.btn}
            >
              View Properties
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

const styles = makeStyles()((theme) => ({
  container: {
    // height: pxToRem(420),
    height: "auto",

    display: "grid",
    gridTemplateRows: "1fr 1fr",
    borderRadius: pxToRem(8),
    overflow: "hidden",
    position: "relative",
    width: "100%",
    gap: theme.spacing(1),
    "&:hover": {
      cursor: "pointer",
    },
    [theme.breakpoints.down(1360)]: {
      width: "100%",
      gap: theme.spacing(1),
      height: pxToRem(410),
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      // maxWidth: "100%",
      // height: "auto",
    },
  },
  top: {
    position: "relative",
  },
  img: {
    borderTopLeftRadius: pxToRem(8),
    borderTopRightRadius: pxToRem(8),
  },
  bottom: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(4),
  },
  price: {
    fontFamily: "Playfair Display",
    fontWeight: 700,
    fontSize: pxToRem(24),
    color: AppColors.primary["300"],
    [theme.breakpoints.down(1360)]: {
      marginBottom: theme.spacing(-2),
    },
  },
  name: {
    fontFamily: "Playfair Display",
    fontWeight: 700,
    fontSize: pxToRem(24),
    color: AppColors.secondary["300"],
  },
  desc: {
    fontWeight: 400,
    fontSize: pxToRem(16),
    color: "#6A6A6A",
    lineHeight: "160%",
    opacity: 0.5,
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(13),
    },
  },
  debug: {
    border: "1px solid green",
  },
  foot: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },
  col: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(1360)]: {
      gap: theme.spacing(1),
    },
  },
  bedTxt: {
    color: AppColors.text.second,
    fontWeight: 500,
    fontSize: 14,
    opacity: 0.5,
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(13),
      fontWeight: 400,
    },
  },
  // overlay
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    justifyContent: "flex-end",
    color: "#fff",
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: 1,
  },
  subOverlay: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    position: "absolute",
    zIndex: 2,
    width: "100%",
    height: "100%",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(4),
    display: "flex",
    opacity: 0,
    transition: "opacity 0.3s ease",
    pointerEvents: "none",
  },
  overlayVisible: {
    opacity: 1,
    pointerEvents: "auto",
  },
  overlayBtn: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
  // Buttons
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
    gap: theme.spacing(4),
  },
  btn: {
    height: pxToRem(46),
  },
  btn1: {
    height: pxToRem(46),
    color: AppColors.neutral.dark,
    background: AppColors.neutral["100"],
    border: `1px solid ${AppColors.neutral["60"]}`,
  },
  btn2: {
    width: pxToRem(140),
    height: pxToRem(46),
    background: AppColors.secondary["100"],
    color: AppColors.neutral.dark,
  },
  buttonsRight: {
    display: "flex",
    gap: theme.spacing(4),
    padding: theme.spacing(4),
    [theme.breakpoints.down(1360)]: {
      gap: theme.spacing(3),
    },
  },
}));
